// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Stack, Tab } from '@mui/material';
// Components, Layouts, Pages
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
import { BaseVerticalTab, Loading, UnderDevelopment } from '~/components/common';
import {
  CreateEstimateAttachmentTab,
  CreateEstimateCustomerTab,
  CreateEstimateFieldsTab,
  CreateEstimateLeadSourceTab,
  CreateEstimateLineItemTab,
  CreateEstimatePrivateNotesTab,
  CreateEstimateScheduleTab,
  CreateEstimateTagsTab,
} from '~/components/module/estimate';
// Others
import { BACK_TO_PREVIOUS_PAGE, DEFAULT_NUMBER_ZERO, EMPTY_STRING } from '~/utils/constants/common';
import { defaultValuesForm, defaultValuesFormMaterial, defaultValuesFormService, schema } from './helper';
import {
  dataTimeZone,
  getCurrentDateUTC,
  getEndTimeOption,
  getFullAddress,
  getNextTimeOption,
  removeEmptyObjects,
} from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IBaseTabItem } from '~/utils/interface/common';
import {
  IBodyUpdateEstimate,
  IBodyUpdateEstimateTags,
  IBodyUpdatePrivateNotes,
  IFormEstimate,
  IMaterialsEstimate,
  IPayloadDetailUpdateEstimate,
  IPhotoAttachmentEstimate,
  IQueryUpdateEstimate,
  IServiceEstimate,
} from '~/utils/interface/estimate';
import {
  PageTypeEnum,
  RoleEnum,
  CreateEstimateTabsEnum,
  TypeTabCreateEstimateEnum,
} from '~/utils/enum/common';
import { StorageEnum } from '~/utils/enum/base';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { createEstimate, getEstimateDetailUpdate, updateEstimate } from '~/thunks/estimate/estimateThunk';
import { varColor } from '~/themes/themeVariables';
import { getJobDetailUpdate } from '~/thunks/job/jobThunk';
import { IJobDetailUpdateReq, IServiceJob } from '~/utils/interface/job';
import { getCustomerDetail } from '~/thunks/customer/customerThunk';
import { convertDataUpdate } from '../formJob/helper';
// Styles, images, icons
import styles from './FormEstimate.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const FormEstimate = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimate', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: estimateId } = useParams();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('jobId');
  const customerId = searchParams.get('customerId');
  const { state } = useLocation();
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  const location = useLocation();
  const { jobIdSchedule } = location.state || {};
  const methods = useForm<IFormEstimate>({
    resolver: yupResolver(schema(t)),
    defaultValues: {
      ...defaultValuesForm,
      ...(estimateId ? {} : { projectedServices: [defaultValuesFormService] }),
      ...(estimateId ? {} : { projectedMaterials: [defaultValuesFormMaterial] }),
      startDate: getCurrentDateUTC(),
      endDate: getCurrentDateUTC(),
      startTime: getNextTimeOption(),
      endTime: getEndTimeOption(getNextTimeOption()),
    },
  });
  const {
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { isDirty },
  } = methods;
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<IFormEstimate>(defaultValuesForm as IFormEstimate);
  const [tabKey, setTabKey] = useState<string>(CreateEstimateTabsEnum.CUSTOMER);
  const [tabItems, setTabItems] = useState<IBaseTabItem[]>([]);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  const getMenuItems = useCallback(() => {
    const items: Array<IBaseTabItem> = [
      {
        label: CreateEstimateTabsEnum.CUSTOMER,
        component: (
          <CreateEstimateCustomerTab onEdit={handleEditEstimate} onCancelEdit={handleCancelFormEstimate} />
        ),
        icon: icons.createJobTabIconCustomer,
      },
    ];

    items.push({
      label: CreateEstimateTabsEnum.SCHEDULE,
      component: (
        <CreateEstimateScheduleTab onEdit={handleEditEstimate} onCancelEdit={handleCancelFormEstimate} />
      ),
      icon: icons.createJobTabIconSchedule,
    });

    items.push({
      label: CreateEstimateTabsEnum.LINE_ITEMS,
      component: (
        <CreateEstimateLineItemTab onEdit={handleEditEstimate} onCancelEdit={handleCancelFormEstimate} />
      ),
      icon: icons.createJobTabIconSchedule,
    });

    items.push({
      label: CreateEstimateTabsEnum.PRIVATE_NOTES,
      component: (
        <CreateEstimatePrivateNotesTab onEdit={handleEditEstimate} onCancelEdit={handleCancelFormEstimate} />
      ),
      icon: icons.createJobTabIconPrivateNotes,
    });

    items.push({
      label: CreateEstimateTabsEnum.FIELDS,
      component: (
        <CreateEstimateFieldsTab onEdit={handleEditEstimate} onCancelEdit={handleCancelFormEstimate} />
      ),
      icon: icons.createJobTabIconJobFields,
    });

    items.push({
      label: CreateEstimateTabsEnum.TAGS,
      component: (
        <CreateEstimateTagsTab onEdit={handleEditEstimate} onCancelEdit={handleCancelFormEstimate} />
      ),
      icon: icons.createJobTabIconTags,
    });

    items.push({
      label: CreateEstimateTabsEnum.LEAD_SOURCE,
      component: (
        <CreateEstimateLeadSourceTab onEdit={handleEditEstimate} onCancelEdit={handleCancelFormEstimate} />
      ),
      icon: icons.createJobTabIconLeadSource,
    });

    items.push({
      label: CreateEstimateTabsEnum.ATTACHMENT,
      component: <CreateEstimateAttachmentTab />,
      icon: icons.createJobTabIconAttachment,
    });

    setTabItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId, initialData]);

  useEffect(() => {
    estimateId && handleGetDetailEstimate(estimateId);
    customerId && handleGetCustomerDetail(customerId);
    jobId && handleGetJobDetail(jobId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId, customerId, jobId]);

  useEffect(() => {
    jobIdSchedule && setValue('jobId', jobIdSchedule);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobIdSchedule]);

  useEffect(() => {
    getMenuItems();
  }, [getMenuItems]);

  useEffect(() => {
    if (!estimateId) return;

    reset(initialData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const calculateTotalLineItem = (projectedServices: IServiceJob[], projectedMaterials: IServiceJob[]) => {
    const total = [...(projectedServices || []), ...(projectedMaterials || [])].reduce((sum, item) => {
      const price = Number(item.price) || DEFAULT_NUMBER_ZERO;
      const quantity = Number(item.quantity) || DEFAULT_NUMBER_ZERO;
      return sum + price * quantity;
    }, DEFAULT_NUMBER_ZERO);

    return total;
  };

  const handleGetJobDetail = (id: string) => {
    const payload: IJobDetailUpdateReq = {
      jobId: id,
      params: {
        timeZone: dataTimeZone(),
      },
    };

    dispatch(getJobDetailUpdate(payload))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const jobDetail = res?.data;

        const assigneeIds: string[] = jobDetail?.schedule?.assignee?.map((assignee) => assignee?.id) || [];
        const jobTagIds: string[] = jobDetail?.jobTags?.map((tag) => tag?.id) || [];

        const newData: IFormEstimate = {
          jobId: jobId || EMPTY_STRING,
          assigneeIds: assigneeIds,
          address: jobDetail?.address || EMPTY_STRING,
          city: jobDetail?.city || EMPTY_STRING,
          state: jobDetail?.state || EMPTY_STRING,
          zipCode: jobDetail?.zipCode || EMPTY_STRING,
          country: jobDetail?.country || EMPTY_STRING,
          latitude: jobDetail?.latitude || DEFAULT_NUMBER_ZERO,
          longitude: jobDetail?.longitude || DEFAULT_NUMBER_ZERO,
          clientId: jobDetail?.client?.id || EMPTY_STRING,
          type: jobDetail?.jobFields?.type || EMPTY_STRING,
          leadSourceId: jobDetail?.leadSource?.id || EMPTY_STRING,
          tagIds: jobTagIds,
          total: calculateTotalLineItem(
            jobDetail?.lineItems?.services || [],
            jobDetail?.lineItems?.materials || []
          ),
          projectedServices:
            jobDetail?.lineItems?.services?.map((service: IServiceEstimate) => {
              return {
                serviceId: service?.serviceId || EMPTY_STRING,
                price: service?.price?.toString() || EMPTY_STRING,
                quantity: service?.quantity?.toString() || EMPTY_STRING,
                totalPrice: service?.totalPrice?.toString() || EMPTY_STRING,
                description: service?.description || EMPTY_STRING,
                cost: service?.cost?.toString() || EMPTY_STRING,
              };
            }) || [],
          projectedMaterials:
            jobDetail?.lineItems?.materials?.map((material: IMaterialsEstimate) => {
              return {
                materialId: material?.materialId || EMPTY_STRING,
                price: material?.price?.toString() || EMPTY_STRING,
                quantity: material?.quantity?.toString() || EMPTY_STRING,
                totalPrice: material?.totalPrice?.toString() || EMPTY_STRING,
                description: material?.description || EMPTY_STRING,
                cost: material?.cost?.toString() || EMPTY_STRING,
              };
            }) || [],
          startDate: jobDetail?.schedule?.startDate || EMPTY_STRING,
          startTime: jobDetail?.schedule?.startTime || EMPTY_STRING,
          endDate: jobDetail?.schedule?.endDate || EMPTY_STRING,
          endTime: jobDetail?.schedule?.endTime || EMPTY_STRING,
          callback: jobDetail?.jobFields?.callBack || false,
          unit: jobDetail?.jobFields?.unit || EMPTY_STRING,
          notes: jobDetail?.privateNotes?.content || EMPTY_STRING,
          timezone: dataTimeZone(),
          folders:
            jobDetail.folders?.map((folder: IPhotoAttachmentEstimate) => {
              return {
                name: folder.name,
                isCurrentApply: folder.isCurrentApply,
              };
            }) || [],
        };

        reset(newData);
      })
      .catch((error) => {})
      .finally(() => {});
  };

  const handleGetCustomerDetail = (customerId: string) => {
    dispatch(getCustomerDetail(customerId))
      .unwrap()
      .then((res) => {
        const { data } = res;
        if (!data) return;

        const billingAddress = data.addresses?.find((item) => item.isBillingAddress === true);

        const newData: IFormEstimate = {
          address: getFullAddress({ ...billingAddress }),
          city: billingAddress?.city || EMPTY_STRING,
          state: billingAddress?.state || EMPTY_STRING,
          zipCode: billingAddress?.zipCode || EMPTY_STRING,
          country: billingAddress?.country || EMPTY_STRING,
          latitude: billingAddress?.latitude || DEFAULT_NUMBER_ZERO,
          longitude: billingAddress?.longitude || DEFAULT_NUMBER_ZERO,
          clientId: data?.id || EMPTY_STRING,
          projectedServices: [defaultValuesFormService],
          projectedMaterials: [defaultValuesFormMaterial],
          timezone: dataTimeZone(),
        };

        reset(newData);
      })
      .catch((err) => {})
      .finally(() => {});
  };

  const handleGetDetailEstimate = (id: string) => {
    setIsLoading(true);

    const payload: IPayloadDetailUpdateEstimate = {
      estimateId: id,
      params: {
        timeZone: dataTimeZone(),
      },
    };
    dispatch(getEstimateDetailUpdate(payload))
      .unwrap()
      .then((res) => {
        if (!res.data) return;

        const estimateDetail = res.data;
        const assigneeIds: string[] =
          estimateDetail?.schedule?.assignees?.map((assignee) => assignee?.id) || [];
        const tagIds: string[] = estimateDetail?.estimateTags?.map((tag) => tag?.id) || [];

        const newData: IFormEstimate = {
          clientId: estimateDetail.client?.id || EMPTY_STRING,
          address: estimateDetail?.address || EMPTY_STRING,
          address2: estimateDetail.address2 || EMPTY_STRING,
          city: estimateDetail?.city || EMPTY_STRING,
          state: estimateDetail?.state || EMPTY_STRING,
          zipCode: estimateDetail?.zipCode || EMPTY_STRING,
          country: estimateDetail?.country || EMPTY_STRING,
          latitude: estimateDetail?.latitude,
          longitude: estimateDetail?.longitude,

          startDate: estimateDetail?.schedule?.startDate || EMPTY_STRING,
          startTime: estimateDetail?.schedule?.startTime || EMPTY_STRING,
          endDate: estimateDetail?.schedule?.endDate || EMPTY_STRING,
          endTime: estimateDetail?.schedule?.endTime || EMPTY_STRING,
          assigneeIds: assigneeIds,
          projectedServices:
            estimateDetail?.lineItems?.services?.map((service: IServiceEstimate) => {
              return {
                id: service?.id || EMPTY_STRING,
                serviceId: service?.serviceId || EMPTY_STRING,
                price: service?.price?.toString() || EMPTY_STRING,
                quantity: service?.quantity?.toString() || EMPTY_STRING,
                totalPrice: service?.totalPrice?.toString() || EMPTY_STRING,
                description: service?.description || EMPTY_STRING,
                cost: service?.cost?.toString() || EMPTY_STRING,
              };
            }) || [],
          projectedMaterials:
            estimateDetail?.lineItems?.materials?.map((material: IMaterialsEstimate) => {
              return {
                id: material?.id || EMPTY_STRING,
                materialId: material?.materialId || EMPTY_STRING,
                price: material?.price?.toString() || EMPTY_STRING,
                quantity: material?.quantity?.toString() || EMPTY_STRING,
                totalPrice: material?.totalPrice?.toString() || EMPTY_STRING,
                description: material?.description || EMPTY_STRING,
                cost: material?.cost?.toString() || EMPTY_STRING,
              };
            }) || [],
          type: estimateDetail?.estimateFields?.type || EMPTY_STRING,
          callback: estimateDetail?.estimateFields?.callBack || false,
          unit: estimateDetail?.estimateFields?.unit || EMPTY_STRING,
          notes: estimateDetail?.privateNotes?.content || EMPTY_STRING,
          tagIds: tagIds ?? [],
          leadSourceId: estimateDetail?.leadSource?.id || EMPTY_STRING,
          privateNotesId: estimateDetail?.privateNotes?.id || EMPTY_STRING,
          folders: estimateDetail.folders ?? [],
          timezone: estimateDetail?.schedule?.timezone || dataTimeZone(),
        };

        setInitialData(newData);
        reset(newData);
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };

  const handleCancelFormEstimate = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        if (state === PageTypeEnum.SCHEDULE) {
          navigate(adminRouteAbsolute.schedules);
          return;
        }

        navigate(BACK_TO_PREVIOUS_PAGE);
        break;

      case RoleEnum.STAFF:
        if (state === PageTypeEnum.SCHEDULE) {
          navigate(staffRouteAbsolute.schedules);
          return;
        }

        navigate(BACK_TO_PREVIOUS_PAGE);
        break;
    }
  };

  const handleAddEstimate = async (data: IFormEstimate) => {
    setIsLoading(true);

    const body: IFormEstimate = removeEmptyObjects(data) as IFormEstimate;

    dispatch(createEstimate(body))
      .unwrap()
      .then(() => {
        handleCancelFormEstimate();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditEstimate = (data: IFormEstimate, type: string) => {
    if (!estimateId) return;
    const { clientId, ...restData } = data;
    let result: Partial<IBodyUpdateEstimate> = {};

    switch (type) {
      case TypeTabCreateEstimateEnum.CUSTOMER:
        result = {
          customer: {
            clientId,
            ...restData,
          },
        };

        break;

      case TypeTabCreateEstimateEnum.SCHEDULE:
        result = {
          schedule: restData,
        };

        break;

      case TypeTabCreateEstimateEnum.LINE_ITEMS:
        const {
          updated: updatedServices,
          created: createdServices,
          deleted: deletedServices,
        } = convertDataUpdate<IServiceEstimate>(data.projectedServices, initialData?.projectedServices ?? []);

        const {
          updated: updatedMaterials,
          created: createdMaterials,
          deleted: deletedMaterials,
        } = convertDataUpdate<IMaterialsEstimate>(data.projectedMaterials, initialData?.projectedMaterials ?? []);

        result = {
          lineItems: {
            estimateServices: {
              deletedServices: deletedServices,
              createdServices: createdServices,
              updatedServices: updatedServices,
            },
            estimateMaterials: {
              deletedMaterials: deletedMaterials,
              createdMaterials: createdMaterials,
              updatedMaterials: updatedMaterials,
            },
            subtotal: restData.subtotal,
            total: restData.total,
          },
        };

        break;

      case TypeTabCreateEstimateEnum.PRIVATE_NOTES:
        const privateNotes: IBodyUpdatePrivateNotes = {
          id: data.privateNotesId || EMPTY_STRING,
          content: data.notes || EMPTY_STRING,
        };
        result = {
          privateNotes: privateNotes,
        };
        break;

      case TypeTabCreateEstimateEnum.FIELDS:
        result = {
          estimateFields: restData,
        };
        break;

      case TypeTabCreateEstimateEnum.TAGS:
        const estimateTags: IBodyUpdateEstimateTags = {
          masterDataIds: data.tagIds || [],
        };
        result = {
          estimateTags: estimateTags,
        };
        break;

      case TypeTabCreateEstimateEnum.LEAD_SOURCE:
        result = {
          leadSource: restData,
        };
        break;

      default:
        break;
    }

    const payload: IQueryUpdateEstimate = {
      estimateId: estimateId,
      body: {
        ...result,
        sectionType: type,
      },
    };

    setIsLoading(true);
    dispatch(updateEstimate(payload))
      .unwrap()
      .then((res) => {
        handleCancelFormEstimate();
      })
      .catch((err) => {})
      .finally(() => setIsLoading(false));
  };

  const handleSubmitFormEstimate = (data: IFormEstimate) => {
    if (!estimateId) {
      handleAddEstimate(data);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabKey(newValue);
  };

  const renderTabContent = () => {
    const item = tabItems.find((tabItem: IBaseTabItem) => {
      return tabKey === tabItem.label;
    });

    return item?.component;
  };
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='formEstimatePage'
      title={estimateId ? t('form_estimate_title_edit') : t('form_estimate_title_add')}
      onBackToPreviousPage={handleCancelFormEstimate}
    >
      <>
        <FormProvider {...methods}>
          <Stack
            component={'form'}
            spacing={'20px'}
            className={cx('form')}
            onSubmit={handleSubmit(handleSubmitFormEstimate)}
          >
            {!estimateId && (
              <Stack
                direction={'row'}
                alignItems={'center'}
                justifyContent={'flex-end'}
                component={'div'}
                spacing={'12px'}
                className={cx('formFooter')}
              >
                <Button variant='outlined' onClick={handleCancelFormEstimate}>
                  {t('common_action_cancel', { ns: 'common/base' })}
                </Button>

                <Button variant='contained' type='submit' disabled={estimateId ? !isDirty : false}>
                  {t('common_action_save', { ns: 'common/base' })}
                </Button>
              </Stack>
            )}

            <Box component={'div'} className={cx('formBody')}>
              <Box component={'div'} className={cx('viewVerticalTab')}>
                <BaseVerticalTab value={tabKey} onChange={handleTabChange}>
                  {tabItems.map((item, index) => (
                    <Tab
                      id='verticalTabItemComponent'
                      value={item.label || EMPTY_STRING}
                      sx={{
                        border: `1px solid ${
                          item.label === tabKey ? varColor.primary : varColor.tabItemBorder
                        }`,
                      }}
                      label={
                        <Stack
                          direction={'row'}
                          spacing={1}
                          alignItems={'center'}
                          justifyContent={'space-between'}
                          style={{ width: '100%' }}
                        >
                          <Stack
                            direction={'row'}
                            spacing={1}
                            alignItems={'center'}
                            className={cx('labelGroup')}
                          >
                            <img
                              src={item.icon}
                              alt={t('common_alt_img', { ns: 'common/base' })}
                              className={cx('tabIcon')}
                            />

                            <span className={cx('tabLabel')}>
                              {t(`tab_label_${item.label}`) || EMPTY_STRING}
                            </span>
                          </Stack>

                          <img
                            src={icons.tabIconPlus}
                            alt={t('common_alt_img', { ns: 'common/base' })}
                            className={cx('tabIcon')}
                          />
                        </Stack>
                      }
                      className={cx('tabItem', { active: tabKey === item.label })}
                      key={index}
                    />
                  ))}
                </BaseVerticalTab>
              </Box>

              <Box component={'div'} className={cx('viewContent')}>
                {renderTabContent()}
              </Box>
            </Box>
          </Stack>
        </FormProvider>

        {isLoading && <Loading />}

        <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
      </>
    </ChildrenPage>
  );
};

export default FormEstimate;
